/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/orgSelection/OrgSelectionPanel" />
import { RepositoryTypes } from "Build/SourceProviders/Common";
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button, IButtonProps } from "VSSUI/Button";
import { Link } from "VSSUI/Link";
import { Tooltip } from "VSSUI/TooltipEx";
import { Location } from "VSSUI/Utilities/Position";
import { ExternalGitRepo } from "Wit/Clients/Internal/Contracts/ExternalEvents";
import { ExternalConnection, GitHubOrgOrUser } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { showConfirmationDialog } from "Wit/Settings/BoardsExternalIntegration/components/common";
import { getTextWithServiceName, getMaxRepoCount } from "Wit/Settings/BoardsExternalIntegration/components/common/Utilities";
import { AddRepoPicker } from "Wit/Settings/BoardsExternalIntegration/components/panels/addRepo/AddRepoPicker";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import { OrgPicker } from "Wit/Settings/BoardsExternalIntegration/components/panels/orgSelection/OrgPicker";
import { IOrgSelectionPanelProps } from "Wit/Settings/BoardsExternalIntegration/components/panels/orgSelection/OrgSelectionPanel.Props";
import { DocUrls, GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";
import { GitHubIntegrationCIActions, publishGitHubIntegrationTelemetry } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import { findExistingGitHubAppConnectionByOrgName } from "Wit/Settings/BoardsExternalIntegration/utils";

export interface IOrgSelectionPanelState {
    isDirty: boolean;
    showRepoPicker?: boolean;
    gitHubOrgOrUser?: GitHubOrgOrUser;
    errorMessage?: string | JSX.Element;
    selectedRepos: ExternalGitRepo[];
    loadingSpinnerMessage?: string;
}

export class OrgSelectionPanel extends VssComponent<IOrgSelectionPanelProps, IOrgSelectionPanelState> {
    private _orgList: GitHubOrgOrUser[];

    constructor(props: IOrgSelectionPanelProps, context: IVssComponentContext) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._orgList = contributionService.getData<GitHubOrgOrUser[]>(GitHubConnectionConstants.GitHubOrgListDataProviderId) || [];
        this.state = {
            isDirty: false,
            selectedRepos: [],
            showRepoPicker: this._orgList.length === 1, // Show repo picker directly if it is one org.
            gitHubOrgOrUser: this._orgList.length === 1 ? this._orgList[0] : undefined,
            loadingSpinnerMessage: undefined
        };

        publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ORG_PANEL_OPEN, {
            orgListCount: this._orgList.length,
            unifiedExperience: true
        });
    }

    public render() {
        // Preselect repo only for new connection. New connection will not service endpoint.
        const shouldPreselectRepo = !!(this.props.connection && !this.props.connection.serviceEndpoint);

        return (
            <BasePanel
                className="boards-org-selection-panel"
                title={this.state.showRepoPicker ? getTextWithServiceName(RepositoryTypes.GitHub, Resources.AddRepo_Title) : Resources.AddOrg_Title}
                onDismissErrorMessage={this._onDismissErrorMessage}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                onDismiss={this._onDismiss}
                errorMessage={this.state.errorMessage}
                loadingSpinnerMessage={this.state.loadingSpinnerMessage}
                showCloseButton={true}
                backButtonProps={
                    this.state.showRepoPicker && this._orgList.length > 1
                        ? ({
                              onClick: this._onBackButtonClicked,
                              subtle: true
                          } as IButtonProps)
                        : undefined
                }
            >
                {this.state.showRepoPicker && this.state.gitHubOrgOrUser ? (
                    <AddRepoPicker
                        isGitHubApp={true}
                        isUnifiedGitHubApp={true}
                        onAddReposSelectionChanged={this._onAddReposSelectionChanged}
                        selectedReposFromSameProvider={this.props.reposFromSameProvider}
                        repositoryType={RepositoryTypes.GitHub}
                        onError={this._onPickerError}
                        orgOrUserName={this.state.gitHubOrgOrUser.name}
                        isUserType={this.state.gitHubOrgOrUser.isUserType}
                        shouldPreselectRepo={shouldPreselectRepo}
                    />
                ) : (
                    <OrgPicker onSelectedOrgChanged={this._onSelectedOrgChanged} orgList={this._orgList} />
                )}
            </BasePanel>
        );
    }

    private readonly _renderHeader = (): JSX.Element => {
        return this.state.showRepoPicker ? (
            <div>{getTextWithServiceName(RepositoryTypes.GitHub, Resources.AddRepo_SubHeader)}</div>
        ) : (
            <div>{Resources.AddOrg_SubHeaderText}</div>
        );
    };

    private readonly _onDismissErrorMessage = () => {
        this.setState({ errorMessage: undefined });
    };

    private readonly _renderFooter = (): JSX.Element | undefined => {
        return this.state.showRepoPicker ? (
            <>
                <div className="align-right">
                    <Button
                        className="footer-button"
                        text={Resources.Save}
                        primary={true}
                        disabled={!this._canSave()}
                        onClick={this._onSaveButtonClicked}
                    />
                </div>
            </>
        ) : undefined;
    };

    private readonly _onSelectedOrgChanged = (gitHubOrgOrUser: GitHubOrgOrUser) => {
        if (findExistingGitHubAppConnectionByOrgName(this.props.existingConnection, gitHubOrgOrUser.name)) {
            this.setState({
                errorMessage: format(Resources.ExistingConnectionInfo, gitHubOrgOrUser.name)
            });
        } else {
            this.setState({
                errorMessage: undefined,
                showRepoPicker: true,
                gitHubOrgOrUser
            });
        }
    };

    private readonly _onAddReposSelectionChanged = (selectedRepos: ExternalGitRepo[]) => {
        this.setState({
            selectedRepos: selectedRepos,
            isDirty: selectedRepos && selectedRepos.length > 0,
            errorMessage: this._isReposCountExceedLimit(selectedRepos)
                ? format(Resources.ExceededMaxReposCountPerConnection, getMaxRepoCount(this.context.pageContext))
                : this.state.errorMessage
        });
    };

    private _isReposCountExceedLimit(selectedRepos: ExternalGitRepo[]): boolean {
        const existingRepoCount = 0;
        const toAddRepoCount = (selectedRepos && selectedRepos.length) || 0;
        const targetRepoCount = existingRepoCount + toAddRepoCount;
        return targetRepoCount > getMaxRepoCount(this.context.pageContext);
    }

    private readonly _onPickerError = (error: Error): void => {
        this.setState({
            errorMessage: error && error.message,
            loadingSpinnerMessage: undefined
        });
    };

    private readonly _onDismiss = () => {
        if (!this.state.loadingSpinnerMessage) {
            if (this.state.isDirty) {
                showConfirmationDialog(
                    this.context,
                    Resources.ConfirmationClosePanelMessage,
                    Resources.ConfirmationClosePanelActionText,
                    Resources.ConfirmationClosePanelTitle,
                    this.props.onDismiss
                );
            } else {
                this.props.onDismiss();
            }
        }
    };

    private readonly _onSaveButtonClicked = () => {
        this.setState({ loadingSpinnerMessage: Resources.SpinnerBasePanelSavingText }, this._onSave);
    };

    private readonly _onSave = async () => {
        const { connection, onSave } = this.props;

        const reposToAdd = [...this.state.selectedRepos];
        const connectionToSave: ExternalConnection = {
            ...connection,
            externalGitRepos: connection.externalGitRepos.concat(reposToAdd)
        };

        let succeeded = true;
        let errorMessage = "";
        let savedConnection: ExternalConnection = null!;
        try {
            savedConnection = await onSave(connectionToSave, this.state.gitHubOrgOrUser!.name);
        } catch (error) {
            succeeded = false;
            errorMessage = error.message;
            this.setState({
                errorMessage: this._getErrorMessageElement(errorMessage),
                loadingSpinnerMessage: undefined
            });
        } finally {
            if (savedConnection && savedConnection.serviceEndpoint) {
                publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.ADMIN_ADD_REPOS, {
                    addedNumberOfRepos: reposToAdd.length,
                    totalNumberOfRepos: savedConnection.externalGitRepos.length,
                    manually: false,
                    providerKey: savedConnection.providerKey,
                    authType: savedConnection.serviceEndpoint.authorizationScheme,
                    succeeded: succeeded,
                    errorMessage: errorMessage,
                    unifiedExperience: true
                });
            }
        }
    };

    private _getErrorMessageElement(errorMessage: string): JSX.Element | string {
        return errorMessage.length < 150 ? (
            errorMessage
        ) : (
            <Tooltip
                anchorOrigin={{ horizontal: Location.center, vertical: Location.end }}
                showOnFocus={true}
                text={errorMessage}
                tooltipOrigin={{ horizontal: Location.center, vertical: Location.start }}
            >
                <div data-is-focusable={true} className="flex">
                    <span className="flex flex-shrink">{`${errorMessage.substring(0, 150)}...`}</span>
                </div>
            </Tooltip>
        );
    }

    private readonly _onBackButtonClicked = () => {
        this.setState({
            showRepoPicker: false,
            loadingSpinnerMessage: undefined
        });
    };

    private _canSave(): boolean {
        return this.state.isDirty && !this._isReposCountExceedLimit(this.state.selectedRepos) && !this.state.errorMessage;
    }
}
